import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import SharedNavbar from "@/components/feature/SharedNavbar";
import ConversionTrustSection from "@/components/feature/ConversionTrustSection";
import SharedFooter from "@/components/feature/SharedFooter";
import Hud2026UpdateBanner from "@/components/feature/Hud2026UpdateBanner";
import MobileStickyApplyCTA from "@/components/feature/MobileStickyApplyCTA";
import { useAttributionParams } from "@/hooks/useAttributionParams";
import DoctorsSection from "@/pages/home/components/DoctorsSection";
import { trackCtaClick } from "@/lib/trackEvent";
import PlannerMarketingSection from "@/components/feature/PlannerMarketingSection";
import EsaHousingGuidesSection from "@/components/feature/EsaHousingGuidesSection";
// ESA-HOUSING-HOMEPAGE-PRICING-PARITY-001 — the canonical homepage pricing
// block. PlanPricingSection is the ONE card implementation; buildEsaPlanCards +
// ESA_PLAN_COPY are the ONE card set, shared with
// src/pages/home/components/HomePricingSection.tsx and the ESA state pages.
// PlanPricingSection renders the shared PaymentTrustStrip itself, which is why
// this page no longer imports or mounts one directly.
import PlanPricingSection from "@/components/feature/PlanPricingSection";
import { buildEsaPlanCards, ESA_PLAN_COPY } from "@/data/planPricingCards";
import {
  getEsaOneTimeTotal,
  getBundleOneTimeTotal,
  getBundleAnnualTotal,
} from "@/config/pricing";
import { useSeoPageContent, renderInline } from "@/seo/content/runtime";
import FaqSchema from "@/seo/content/FaqSchema";
import { bulletCollection, cardCollection, col, definePageContent, f, faqCollection } from "@/seo/content/define";

// PAWTENANT-ESA-HOUSING-CRO-RAW-HTML-LEGAL-001 — page-level funnel analytics.
//
// `cta_click` is an EXISTING canonical event name in src/lib/trackEvent.ts and
// `page_view` already fires for every route from DeferredServices, so this page
// adds NO new event name and NO new conversion action. The placement id says
// WHERE the click happened. Raw click identifiers are never passed here:
// trackEvent's own enrichment attaches attribution server-side, and nothing
// about the ad click is written into the DOM.
const CTA_HERO = "esa_housing_hero";
const CTA_STICKY = "esa_housing_sticky";
const CTA_SECTION = "esa_housing_section";

// ── SEO / indexability contract ─────────────────────────────────────────────
//
// PAWTENANT-ESA-HOUSING-CRO-RAW-HTML-LEGAL-001 (owner, 2026-09-17) REPLACES the
// 2026-07-28 "noindex by policy" decision for this route.
//
// What the old policy actually shipped: the page was absent from CORE_PAGE_META,
// so it had no prerendered file and was served by the Vercel catch-all as
// out/app.html — raw HTML carrying the HOMEPAGE title, the HOMEPAGE canonical
// (https://pawtenant.com/), `robots: index, follow` and zero <h1>. The
// noindex,nofollow meta was applied by JavaScript AFTER hydration, which a
// non-JS crawler never sees. The route was therefore crawlable, indexable and
// self-canonicalising to the homepage — not excluded.
//
// The route is now a first-class member of the prerender contract:
//   - src/config/seoConfig.ts    → CORE_PAGE_META["/esa-letter-housing"]
//   - src/prerender/entry.tsx    → real component SSR into <div id="root">
//   - public/sitemap.xml         → listed exactly once
//   - scripts/data/h1-coverage-routes.json → raw-HTML <h1> coverage
//
// So the page no longer writes a robots meta of its own. index.html already
// ships `index, follow, …` and prerender-seo.mjs writes the self-referencing
// canonical; a second robots tag here would only be able to disagree with it.
// The title/description are owned by seoConfig (one source, head + runtime).
// scripts/check-esa-housing-indexability.mjs fails the build if any part of
// that contract regresses.

// FAQ list — the SINGLE source for both the visible accordion and the FAQPage
// JSON-LD below it, so the two can never disagree. Wording is compliance-safe:
// a licensed professional decides qualification after an individual evaluation,
// approval is never automatic, and no housing outcome is promised.
//
// PAWTENANT-CONTENT-STUDIO-INTEGRATION-001: these are the compiled DEFAULTS of
// the `faq.items` collection below. Inside the component the resolved list
// (published overrides applied) is read once as `faqs`, and BOTH the accordion
// and <FaqSchema> read that one list.
const FAQ_ITEMS = [
  {
    q: "Who may qualify for an ESA housing letter?",
    a: "You may qualify if you have a mental or emotional health condition and a licensed professional determines, after reviewing your individual assessment, that an emotional support animal supports your wellbeing. Qualification is a clinical decision made by the reviewing professional — it is never automatic and never decided by an algorithm. If you do not qualify, your payment is refunded.",
  },
  {
    q: "Does a landlord have to accept every accommodation request?",
    a: "No. Housing providers covered by the federal Fair Housing Act generally must consider a reasonable accommodation request individually rather than apply a blanket no-pet rule, but they may deny a request in certain circumstances, and some housing is not covered at all. Documentation supports a request; it does not decide the outcome. The decision remains with the housing provider, and PawTenant cannot guarantee acceptance, a fee waiver, or any other result.",
  },
  {
    q: "What does the documentation contain?",
    a: "Housing-focused ESA documentation naming you and your animal, written for a reasonable accommodation request. It carries the reviewing professional's full name and credential, their state license number and NPI, their signature, the issue and expiration dates, and a verification ID in the format ESA-XX-XXXXXXX that a landlord can confirm at pawtenant.com/verify. It does not disclose your diagnosis or clinical notes.",
  },
  {
    q: "How long does it take?",
    a: "The online assessment takes about five minutes. A licensed professional then reviews it, and documentation is typically delivered within 24 hours after provider review, if approved. Review timing depends on the reviewing professional, and approval is never guaranteed.",
  },
  {
    q: "What happens if I do not qualify?",
    a: "If the reviewing professional does not find an emotional support animal clinically appropriate for you, no letter is issued and your payment is refunded. There is no charge retained for an evaluation that does not lead to documentation.",
  },
  {
    q: "Can one letter cover more than one animal?",
    a: "Yes, where it is clinically appropriate. The one-time package covers up to 2 pets on a single document, and three pets are covered at a fixed total. Whether multiple animals are supported is part of the professional's clinical judgement, not an automatic add-on.",
  },
  {
    q: "Do I need to renew each year?",
    a: "Many housing providers ask for current documentation, so most people renew annually. You can buy a one-time letter and return later, or choose the annual plan, which renews automatically at the renewal price shown and can be cancelled at any time from your account portal.",
  },
  {
    q: "What is the difference between an ESA letter and a psychiatric service dog letter?",
    a: "An emotional support animal provides comfort through companionship and is relevant to housing accommodation requests only. A psychiatric service dog is a dog individually trained to perform tasks related to a person's disability; comfort alone is not a trained task. PawTenant offers documentation for both, but neither document certifies an animal or creates public-access rights.",
  },
  {
    q: "Does an ESA letter give my animal access to stores, restaurants or flights?",
    a: "No. Under the Americans with Disabilities Act, emotional support animals are not service animals, so ESA documentation does not create public-access rights and businesses are not required to admit an emotional support animal. Air travel follows the airline's own rules and current U.S. Department of Transportation forms. This documentation is for housing accommodation requests.",
  },
  {
    q: "What makes an ESA letter valid and verifiable?",
    a: "A valid and verifiable ESA letter names a professional licensed in your state, carries their credential, state license number and NPI, and is signed and dated. It also carries a verification ID in the format ESA-XX-XXXXXXX, so a housing provider can confirm the document at pawtenant.com/verify. Verification confirms authenticity and an active licence only — it never exposes your diagnosis or your assessment answers. Whether documentation is issued at all remains the reviewing professional's individual clinical decision.",
  },
  {
    q: "Can an ESA letter cover a dog or cat?",
    a: "Yes, where it is clinically appropriate. Dogs and cats are both commonly considered, and the reviewing professional decides after reading your individual assessment whether an emotional support animal supports your mental or emotional health. The one-time package covers up to 2 pets on a single document. Qualification is never automatic, and your animal is named on the documentation rather than certified or registered.",
  },
  {
    q: "Is an ESA letter the same as an ESA prescription letter?",
    a: "“ESA prescription letter” is consumer search wording rather than the clinically preferred name, and it is not a separate product. An emotional support animal is not medication, and PawTenant does not prescribe anything. What a licensed professional may issue, when it is clinically appropriate after an individual review, is documentation that supports a reasonable accommodation request to a housing provider.",
  },
  {
    q: "Does an emotional support animal need registration?",
    a: "No. There is no official federal register of emotional support animals. A registry listing, certificate, ID card or vest does not replace documentation from a professional licensed in your state, and buying one proves nothing to a housing provider. What a housing provider may ask for is that documentation, which the reviewing professional issues only when an emotional support animal is clinically appropriate.",
  },
  {
    q: "How can I identify a real ESA letter online?",
    a: "Look for an individual review by a professional licensed in your state, the provider's full credentials and license details printed on the document, transparent pricing, and a way for a housing provider to verify it. A legitimate ESA letter is the outcome of a clinical review rather than something you simply buy, so instant approval, a purchased certificate or automatic registration are all signs to avoid.",
  },
];

// ---------------------------------------------------------------------------
// SECTION MAP -- PAWTENANT-CONTENT-STUDIO-INTEGRATION-001 (binding ported from
// TEST's PAWTENANT-SEO-EDITOR-V3-NOCODE-WORKSPACE-001; copy is LIVE's own).
//
// Every visible section of this page, in render order, with every string it
// shows. The component renders THROUGH this map (useSeoPageContent), so a
// field listed here is bound by construction. The defaults below are the
// LIVE page's compiled copy; a Content Studio override (frozen into
// src/generated/cmsBundle.json at build time) replaces one field at a time.
// Prices are derived from src/config/pricing.ts and declared managed, the
// legal / refund disclosures are managed, and this page's CTA destinations
// are attribution-wrapped in code, so no destination field exists here.
// Kept in THIS file, next to the markup, so the repo's copy guards keep
// scanning the same file they always did.
// ---------------------------------------------------------------------------

export const ESA_HOUSING_CONTENT = definePageContent("/esa-letter-housing", 1, [
  {
    id: "hero",
    label: "Hero",
    sectionType: "hero",
    description: "The frozen hero: headline, supporting copy, offer summary, trust chips, the sample-letter card and the phone photo. Managed by website design: its wording is pinned byte-for-byte by the housing guards (scripts/check-esa-seo-keywords-live.mjs), so nothing here is editable.",
    managed: true,
    fields: {
      heading: f.managed("Main heading (H1)", "The frozen housing headline", { help: "Managed by website design: the hero wording is frozen by the housing guards." }),
      description: f.managed("Supporting copy", "The frozen desktop paragraph and the phone-only supporting line"),
      offer: f.managed("Offer summary and price line", "Online assessment, timing and the one-time price from the pricing configuration"),
      ctas: f.managed("Buttons", "Start Free Assessment (attribution-wrapped) and See How It Works"),
      trust: f.managed("Trust signals", "The phone trust list and the four desktop trust chips"),
      letter_card: f.managed("Sample letter card", "The sample ESA letter document card", { help: "Managed by website design: the measured largest-paint element on desktop." }),
      mobile_photo: f.managed("Phone hero photo", "The owner-and-dog photo behind the phone hero", { help: "Managed by website design: the largest-paint element on phones, with a tuned crop pinned by the housing guards." }),
    },
  },
  {
    id: "quick_answer",
    label: "What an ESA housing letter is",
    sectionType: "content",
    description: "Heading, paragraph and the two lists. The photo is managed.",
    fields: {
      heading: f.heading("Heading (H2)", "What an ESA housing letter is"),
      paragraph: f.paragraph("Paragraph", "An emotional support animal letter is documentation from a licensed mental health professional stating that an animal supports a mental or emotional health condition. Tenants use it to ask a housing provider for a reasonable accommodation to a pet rule under the Fair Housing Act. It is a clinical document that supports a request — nothing more, and nothing less."),
      photo: f.managed("Photo", "Woman at home in her apartment with her dog resting on her lap", { help: "Managed by website design: a local lifestyle asset whose path, size and order are pinned by the housing guards." }),
      supports_title: f.heading("Green list title", "What it may support", { constraints: { maxLength: 50 } }),
      not_title: f.heading("Grey list title", "What it does not do", { constraints: { maxLength: 50 } }),
    },
    collections: {
      supports: bulletCollection("Green list", [
        "A reasonable accommodation request to a covered housing provider",
        "A request that a no-pet rule, breed rule or weight rule be reconsidered for your animal",
        "A landlord's request for documentation from a licensed professional",
      ], { maxItems: 6 }),
      not: bulletCollection("Grey list", [
        "It does not decide or guarantee a landlord's answer",
        "It does not create public-access rights in stores, restaurants, hotels or workplaces",
        "It does not certify, register or license an animal",
        "It does not remove every pet fee, and it does not apply to every property",
      ], { maxItems: 6 }),
    },
  },
  {
    id: "how_it_works",
    label: "How it works",
    sectionType: "cards",
    description: "Heading and the three steps.",
    fields: { heading: f.heading("Heading (H2)", "How it works") },
    collections: {
      steps: cardCollection("Steps", [col("key", "Key", "badge", { managed: true }), col("title", "Title", "heading", { constraints: { maxLength: 60 } }), col("body", "Text", "paragraph", { constraints: { maxLength: 260 } })], [
        { key: "s1", title: "Complete the assessment", body: "A confidential online questionnaire about your situation and your animal. It takes about five minutes and starting it is free." },
        { key: "s2", title: "A licensed professional reviews it", body: "A professional licensed in your state reviews your assessment individually and may follow up for more detail. Qualification is their clinical decision." },
        { key: "s3", title: "Receive documentation if appropriate", body: "If an emotional support animal is clinically appropriate, your documentation arrives as a secure PDF, typically within 24 hours after provider review. If not, you are refunded." },
      ]),
    },
  },
  {
    id: "whats_included",
    label: "What the documentation includes",
    sectionType: "content",
    description: "Heading, intro and the two grouped cards with their eight icon lines. Managed by website design: the layout and the eight documentation lines are pinned by scripts/check-esa-psd-provider-cards.mjs.",
    managed: true,
    fields: {
      heading: f.managed("Heading (H2)", "The documentation heading"),
      intro: f.managed("Introduction", "The documentation intro paragraph"),
      groups: f.managed("Grouped cards", "Two DocGroup cards: four lines from the reviewing professional, four lines about you and verification"),
    },
  },
  {
    id: "licensed_professionals",
    label: "Reviewed by licensed professionals",
    sectionType: "content",
    description: "The provider carousel: the homepage's shared DoctorsSection, gated by the live provider directory. Nothing here is editable.",
    managed: true,
    fields: {
      eyebrow: f.managed("Eyebrow", "Licensed Professionals", { help: "Managed by website design: the provider section is the homepage's shared DoctorsSection." }),
      heading: f.managed("Heading (H2)", "Reviewed by licensed professionals"),
      intro: f.managed("Introduction", "Every ESA housing letter is reviewed and signed by a licensed mental health professional, with a public license record you can check yourself."),
      providers: f.managed("Provider cards", "Featured providers from the live provider directory", { help: "Managed by website design: the cards come from the provider directory." }),
      footnote: f.managed("Note under the carousel", "Your assessment is matched to a professional licensed in your own state, who may or may not be one of the professionals shown here. Every license and NPI can be confirmed independently on the public NPPES registry."),
    },
  },
  {
    id: "housing_use",
    label: "Using the documentation for a housing request",
    sectionType: "content",
    description: "Heading, intro and the two lists. The photo and the legal note are managed.",
    fields: {
      heading: f.heading("Heading (H2)", "Using the documentation for a housing request"),
      intro: f.paragraph("Introduction", "Under the Fair Housing Act, a covered housing provider is generally expected to consider a reasonable accommodation request individually, rather than apply a blanket pet rule without looking at the request. Your documentation is what you attach to that request. What happens next is the housing provider's decision."),
      photo: f.managed("Photo", "Tabby cat asleep on a sunny apartment windowsill", { help: "Managed by website design: a local lifestyle asset whose path, size and order are pinned by the housing guards." }),
      use_title: f.heading("First list title", "How people usually use it", { constraints: { maxLength: 50 } }),
      claims_title: f.heading("Second list title", "What we do not claim", { constraints: { maxLength: 50 } }),
      disclaimer: f.managed("Legal note", "The not-a-law-firm legal note", { help: "Managed by website design: legal disclaimer copy." }),
    },
    collections: {
      use: bulletCollection("First list", [
        "Send it with a written accommodation request to the landlord, property manager or HOA",
        "Provide it when a housing provider asks for documentation from a licensed professional",
        "Keep the verification ID handy so the housing provider can confirm the document themselves",
      ], { maxItems: 6 }),
      claims: bulletCollection("Second list", [
        "Not every property is covered by the Fair Housing Act",
        "Not every landlord must approve a request, and a request can be denied",
        "Pet fees and pet rent are not always removed",
        "Short-term and vacation rentals are treated differently and are often not covered",
      ], { maxItems: 6 }),
    },
  },
  {
    id: "pricing",
    label: "Transparent pricing",
    sectionType: "custom",
    description: "The shared homepage pricing block.",
    managed: true,
    fields: { component: f.managed("Pricing cards", "Shared component: PlanPricingSection (ESA), plan cards from src/data/planPricingCards", { help: "Managed by website design: one card implementation shared with the homepage." }) },
  },
  {
    id: "pricing_notes",
    label: "Pricing notes",
    sectionType: "content",
    description: "The accommodation-document note, the Klarna line and the PSD line under the pricing cards.",
    fields: {
      ra_title: f.heading("Note title", "Optional accommodation-document support", { constraints: { maxLength: 60 } }),
      ra_body: f.managed("Note text", "The accommodation-form support note", { help: "Managed by website design: carries figures from the pricing configuration." }),
      klarna: f.managed("Klarna line", "The Klarna availability disclosure", { help: "Managed by website design: an instalment disclosure." }),
      psd: f.managed("PSD line", "The psychiatric-service-dog pointer", { help: "Managed by website design: carries the attribution-safe PSD assessment link." }),
    },
  },
  {
    id: "why_pawtenant",
    label: "Why tenants choose PawTenant",
    sectionType: "cards",
    description: "Heading and four reason cards.",
    fields: { heading: f.heading("Heading (H2)", "Why tenants choose PawTenant") },
    collections: {
      cards: cardCollection("Reason cards", [col("key", "Key", "badge", { managed: true }), col("title", "Title", "heading", { constraints: { maxLength: 60 } }), col("body", "Text", "paragraph", { constraints: { maxLength: 260 } })], [
        { key: "review", title: "A real clinical review, every time", body: "Each ESA letter from a licensed therapist or other qualified professional follows an individual review by someone licensed in your state. Approval is never automatic, and documentation is issued only when clinically appropriate." },
        { key: "verify", title: "Documentation a landlord can check", body: "Each document carries the professional's name, license number and NPI, plus a verification ID a housing provider can confirm directly — without seeing any clinical detail." },
        { key: "refund", title: "Refund if you do not qualify", body: "If the reviewing professional does not approve documentation, no letter is issued and your payment is refunded." },
        { key: "honest", title: "Honest about what a letter can do", body: "We do not promise landlord approval, waived pet fees, or access to places an emotional support animal has no right to enter." },
      ]),
    },
  },
  {
    id: "verify",
    label: "What a landlord can verify",
    sectionType: "content",
    description: "Heading, paragraph and the three verification steps. The screenshot is managed.",
    fields: {
      heading: f.heading("Heading (H2)", "What makes an ESA letter valid and verifiable"),
      paragraph: f.paragraph("Paragraph", "A valid and verifiable ESA letter should come from a professional licensed in your state, carry their current credentials, and give a housing provider a way to confirm the document without seeing private clinical information. Your landlord can verify the documentation without ever seeing your diagnosis, your answers, or any clinical note. The verification page confirms authenticity only."),
      screenshot: f.managed("Verification screenshot", "The owner's verification result screenshot", { help: "Managed by website design: served byte-exact." }),
    },
    collections: {
      steps: cardCollection("Verification steps", [col("key", "Key", "badge", { managed: true }), col("title", "Title", "heading", { constraints: { maxLength: 60 } }), col("body", "Text", "paragraph", { constraints: { maxLength: 200 } })], [
        { key: "v1", title: "They open the verification link", body: "Printed on the document, alongside the ESA-XX-XXXXXXX verification ID." },
        { key: "v2", title: "The page confirms the document is genuine", body: "Document type, state, issue and expiration dates, and the issuing professional's credentials." },
        { key: "v3", title: "Nothing clinical is shown", body: "No diagnosis, no assessment answers, no medical record — only whether the document is authentic and the licence is active." },
      ]),
    },
  },
  {
    id: "faq",
    label: "Frequently asked questions",
    sectionType: "faq",
    description: "Heading and every question and answer. The FAQPage structured data is generated from the same list.",
    fields: {
      heading: f.heading("Heading (H2)", "Frequently asked questions"),
      show_more_label: f.ctaLabel("Show-more button (phones)", "Show more questions"),
    },
    collections: {
      items: faqCollection(FAQ_ITEMS),
    },
  },
  {
    id: "final_cta",
    label: "See whether you qualify (final call to action)",
    sectionType: "cta",
    description: "The closing dark band: heading, paragraph, button and three trust lines.",
    fields: {
      heading: f.heading("Heading (H2)", "See whether you qualify — it takes about five minutes"),
      description: f.paragraph("Paragraph", "A professional licensed in your state reviews your assessment individually. Documentation is issued only when it is clinically appropriate, and you are refunded if you do not qualify."),
      button_label: f.ctaLabel("Button label", "Start Free Assessment", { where: "Orange button; goes to the assessment with ad attribution." }),
    },
    collections: {
      trust: bulletCollection("Trust lines", [
        "Refund if you do not qualify after review",
        "Verification ID on every document",
        "Licence number and NPI on every document",
      ], { minItems: 1, maxItems: 3 }),
    },
  },
  {
    id: "resources",
    label: "ESA housing resources",
    sectionType: "custom",
    description: "Shared links section to the three ESA housing guides.",
    managed: true,
    fields: {
      heading: f.managed("Heading", "ESA housing resources", { help: "Managed by website design: the shared EsaHousingGuidesSection." }),
      intro: f.managed("Introduction", "Practical guides to documentation, the accommodation process, and common mistakes to avoid."),
    },
  },
  {
    id: "planner",
    label: "Free Pet Care Planner",
    sectionType: "custom",
    description: "Shared planner marketing section.",
    managed: true,
    fields: { component: f.managed("Planner section", "Shared component: PlannerMarketingSection (ESA)") },
  },
  {
    id: "hud_banner",
    label: "2026 HUD update banner",
    sectionType: "custom",
    description: "Shared HUD update banner.",
    managed: true,
    fields: { component: f.managed("HUD banner", "Shared component: Hud2026UpdateBanner") },
  },
]);

// Prices come from src/config/pricing.ts — the ONE source of truth, the same
// module the checkout quote and the Stripe amounts are mirrored from. Nothing
// on this page hardcodes a dollar amount, so a price change lands here without
// an edit and this page can never quote an amount the checkout will not honour.
// The hero offer line and the RA note are the only figures this page states
// outside the shared cards; the cards derive their own from the same module.
const ESA_ONE_TIME = `$${getEsaOneTimeTotal(1)}`;
const RA_ONE_TIME = `$${getBundleOneTimeTotal()}`;
const RA_ANNUAL = `$${getBundleAnnualTotal()}`;

export default function LpEsaHousingPage() {
  const c = useSeoPageContent(ESA_HOUSING_CONTENT);
  // The resolved FAQ list (published overrides applied). The accordion and the
  // FAQPage schema (<FaqSchema>) both read THIS list, so the visible questions
  // and the structured data cannot disagree, and with no override it is
  // exactly FAQ_ITEMS above.
  const faqs = c.faqs("faq.items");
  const mobileTrust = c.items("hero.mobile_trust");
  const trustChips = c.items("hero.trust_chips");
  const supports = c.items("quick_answer.supports");
  const notList = c.items("quick_answer.not");
  const steps = c.items("how_it_works.steps");
  const includedLeft = c.items("whats_included.left");
  const includedRight = c.items("whats_included.right");
  const useList = c.items("housing_use.use");
  const claimsList = c.items("housing_use.claims");
  const reasons = c.items("why_pawtenant.cards");
  const verifySteps = c.items("verify.steps");
  const finalTrust = c.items("final_cta.trust");

  // Attribution-safe CTA destinations. appendAttribution() merges into an
  // existing query string, so the subscription link keeps `plan=subscription`
  // AND the ad params (gclid / gbraid / wbraid / UTM).
  const { withAttribution } = useAttributionParams();
  const ASSESSMENT_HREF = withAttribution("/assessment");
  const PSD_ASSESSMENT_HREF = withAttribution("/psd-assessment");
  // ESA-HOUSING-HOMEPAGE-PRICING-PARITY-001 — there is no longer a page-local
  // `?plan=subscription` CTA. The annual plan is now chosen from the shared
  // homepage card, whose CTA routes to the plain /assessment exactly as it does
  // on the homepage; the plan is picked inside the assessment. Reintroducing a
  // page-local subscription deep link here would make this page's CTAs behave
  // differently from the same card on the homepage.

  // Mobile-only: show the first four FAQs initially, the rest behind a toggle.
  // Every item stays in the DOM regardless (display:none only), so the raw
  // prerendered HTML and the JSON-LD always carry the full list.
  const [showAllMobile, setShowAllMobile] = useState(false);

  // The <head> title/description/canonical for this route are owned by
  // seoConfig + prerender-seo.mjs + the runtime SEOManager. The ONLY structured
  // data this page adds is the FAQPage schema, emitted by <FaqSchema> below
  // from the same resolved `faqs` list the accordion renders. The build-time
  // prerender writes the identical object into <head>; when that head script
  // is present <FaqSchema> renders nothing, so exactly one FAQPage ships
  // either way.

  return (
    <main className="bg-[#FAFAFA] text-slate-900 antialiased">
      <FaqSchema routePath="/esa-letter-housing" items={faqs} />

      {/* ─────────── 0. Site-wide navbar ─────────── */}
      <SharedNavbar />

      {/* ─────────── 1. HERO ───────────
          ESA-HOUSING-MOBILE-HERO-LIVE-001 (owner, 2026-09-20)
          ONE hero section, ONE <h1>, two presentations:
            • md and up  — the desktop hero is unchanged: badge, paragraph,
              offer-summary row, two buttons, payment/refund note, four-card
              trust grid and the sample-document preview.
            • below md   — an image-led compact hero: the local owner-and-dog
              photo as a full-bleed background under a dark green/slate
              overlay, the H1, one supporting line, three trust signals and
              ONE orange CTA. The document preview, offer-summary row,
              "See How It Works", the payment/legal paragraph and the
              four-card grid are display:none here.
          Every mobile-only block carries md:hidden and every desktop-only
          block carries hidden md:…, so the DOM holds each element exactly
          once and the raw prerendered HTML still has a single <h1>.
          The photo is a positioned <img>, never a CSS background rule:
          scripts/check-esa-housing-redesign.mjs pins it to the md:hidden
          wrapper so it can never leak onto the desktop layout. */}
      <section className="relative overflow-hidden md:overflow-visible bg-[#0E2A47] md:bg-white border-b border-slate-200">
        {/* Mobile-only photo + overlay. aria-hidden: purely decorative, the
            heading carries the meaning. Eager because it is the mobile LCP. */}
        <div data-pt-hero="mobile-photo" className="md:hidden absolute inset-0 z-0 pointer-events-none" aria-hidden>
          <img
            src="/assets/lifestyle/esa-housing-hero-owner-dog.webp"
            alt=""
            width={1600}
            height={900}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover object-[83%_center]"
          />
          {/* ESA-PSD-PROVIDER-CARDS-LIVE-001: 83% horizontal. The 16:9 photo is
              height-bound in the tall mobile hero, so only ~40% of its width
              shows; the woman and the dog sit at 55–84% of it. The old centre
              crop showed empty wall with the woman cut at the right edge and
              no dog. Tuned in the browser at 390px and 425px. This wrapper is
              md:hidden, so desktop and tablet (md+) never render this image. */}
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(9,42,38,0.72)] via-[rgba(14,42,71,0.82)] to-[rgba(14,42,71,0.94)]" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-5 pt-24 md:pt-28 pb-12 md:pb-20 grid md:grid-cols-12 gap-10 md:gap-14 items-start md:items-center">
          <div className="md:col-span-7">
            <span className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.08em] uppercase text-emerald-800 bg-emerald-100 border border-emerald-300 px-2.5 py-1 rounded-full mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              Housing-related ESA documentation
            </span>

            <h1 className="text-[30px] sm:text-[34px] md:text-[40px] lg:text-[44px] leading-[1.12] font-bold tracking-tight text-white md:text-slate-900 mb-4 [text-shadow:0_1px_2px_rgba(0,0,0,0.25)] md:[text-shadow:none]">
              ESA Letter for Housing From a Licensed Professional
            </h1>

            {/* Desktop supporting paragraph (unchanged apart from the closing
                sentence, which the owner replaced on 2026-09-20). */}
            <p className="hidden md:block text-[16px] md:text-[17px] leading-relaxed text-slate-600 mb-5 max-w-[62ch]">
              Complete a confidential online assessment. A professional licensed
              in your state reviews it individually and issues housing-focused
              documentation only when an emotional support animal is clinically
              appropriate, with a 24-hour fastest turn around time and the
              highest acceptance rate in the healthcare industry.
            </p>

            {/* Mobile supporting line — the ONLY body copy in the mobile hero. */}
            <p data-pt-hero="mobile-support" className="md:hidden text-[16px] leading-relaxed text-white/85 mb-5 max-w-[40ch]">
              With a 24-hour fastest turn around time and the highest
              acceptance rate in the healthcare industry.
            </p>

            <ul className="hidden md:flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[14px] md:text-[15px] leading-snug text-slate-700 mb-6 max-w-xl list-none p-0 m-0" aria-label="Offer summary">
              <li className="inline-flex items-center gap-1.5">
                <OfferTick />
                <span className="font-semibold text-slate-900">Online assessment</span>
              </li>
              <li className="inline-flex items-center gap-1.5">
                <OfferTick />
                <span>about 5 minutes</span>
              </li>
              <li className="inline-flex items-center gap-1.5">
                <OfferTick />
                <span>
                  <span className="font-semibold text-slate-900">{ESA_ONE_TIME}</span> one-time, up to 2 pets
                </span>
              </li>
            </ul>

            {/* Mobile trust signals — exactly three. */}
            <ul data-pt-hero="mobile-trust" className="md:hidden flex flex-col gap-2.5 text-[15px] leading-snug text-white mb-6 list-none p-0 m-0" aria-label="Why PawTenant">
              <li className="flex items-center gap-2.5">
                <MobileTick />
                <span>Licensed professional review</span>
              </li>
              <li className="flex items-center gap-2.5">
                <MobileTick />
                <span>24-hour turnaround</span>
              </li>
              <li className="flex items-center gap-2.5">
                <MobileTick />
                <span>Refund if you do not qualify</span>
              </li>
            </ul>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Link
                to={ASSESSMENT_HREF}
                onClick={() => trackCtaClick(CTA_HERO)}
                className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-[15px] md:text-[16px] px-7 py-4 rounded-md transition shadow-[0_4px_16px_rgba(249,115,22,0.30)]"
              >
                Start Free Assessment
                <span aria-hidden>→</span>
              </Link>
              <a
                href="#how-it-works"
                className="hidden md:inline-flex items-center justify-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-[15px] md:text-[16px] px-7 py-4 rounded-md transition"
              >
                See How It Works
              </a>
            </div>

            {/* "Free" describes the ASSESSMENT ONLY. This line exists so the
                CTA above can never be read as a free letter: the customer pays
                at checkout, before clinical review, and is refunded when the
                reviewing professional does not approve documentation. */}
            <p className="hidden md:block text-[13px] text-slate-500 leading-relaxed mb-7 max-w-[62ch]">
              Starting the assessment is free. You pay when you choose a package
              at checkout, and you are refunded if you do not qualify. The
              assessment fee covers the review — it does not buy a letter.
            </p>

            <div className="hidden md:grid md:grid-cols-2 gap-4 max-w-xl">
              <TrustChip
                label="Licensed provider review"
                detail="Reviewed by a professional licensed in your state"
                icon="badge"
              />
              <TrustChip
                label="Typically within 24 hours after provider review"
                detail="Delivered as a secure PDF, if approved"
                tone="green"
                icon="shield"
              />
              <TrustChip
                label="Refund if you do not qualify"
                detail="No letter issued, payment returned"
                icon="refund"
              />
              <TrustChip
                label="Secure and confidential"
                detail="Your clinical answers are never shown to a landlord"
                tone="green"
                icon="shield"
              />
            </div>
          </div>

          <div data-pt-hero="desktop-preview" className="hidden md:block md:col-span-5">
            <LetterPreviewCard />
          </div>
        </div>
      </section>

      {/* Conversion trust section — PAWTENANT-TRUST-SECTION-LIVE-ROLLOUT-002.
          Shared, non-interactive apart from the two pinned review links;
          mounted once, directly after the hero. */}
      <ConversionTrustSection />

      {/* ─────────── 2. QUICK ANSWER ─────────── */}
      <section id="quick-answer" data-seo-section="quick_answer" className="scroll-mt-24 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16">
          <div className="md:grid md:grid-cols-12 md:gap-10 md:items-center mb-8">
            <div className="md:col-span-7">
              <h2 data-seo-field="quick_answer.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4 leading-[1.18]">
                {c.text("quick_answer.heading")}
              </h2>
              <p data-seo-field="quick_answer.paragraph" className="text-[15px] md:text-[16px] text-slate-600 leading-relaxed max-w-[68ch] mb-6 md:mb-0">
                {renderInline(c.text("quick_answer.paragraph"))}
              </p>
            </div>
            <SectionPhoto
              className="md:col-span-5"
              webp="/assets/lifestyle/esa-owner-dog-apartment.webp"
              jpg="/assets/lifestyle/esa-owner-dog-apartment.jpg"
              alt="Woman at home in her apartment with her dog resting on her lap"
              width={1200}
              height={800}
              aspect="aspect-[3/2]"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-5">
              <div data-seo-field="quick_answer.supports_title" className="text-[14px] font-semibold text-emerald-900 mb-2">{c.text("quick_answer.supports_title")}</div>
              <ul className="space-y-2 list-none p-0 m-0" data-seo-collection="quick_answer.supports">
                {supports.map((item, i) => <PlainPoint key={i} tone="green">{item.text}</PlainPoint>)}
              </ul>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <div data-seo-field="quick_answer.not_title" className="text-[14px] font-semibold text-slate-900 mb-2">{c.text("quick_answer.not_title")}</div>
              <ul className="space-y-2 list-none p-0 m-0" data-seo-collection="quick_answer.not">
                {notList.map((item, i) => <PlainPoint key={i}>{item.text}</PlainPoint>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── 3. HOW IT WORKS ─────────── */}
      <section id="how-it-works" data-seo-section="how_it_works" className="scroll-mt-24 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16">
          <h2 data-seo-field="how_it_works.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.18]">
            {c.text("how_it_works.heading")}
          </h2>
          <ol className="grid md:grid-cols-3 gap-6 list-none p-0 m-0" data-seo-collection="how_it_works.steps">
            {steps.map((step, i) => (
              <Step key={step.key} n={i + 1} title={step.title}>
                {renderInline(step.body)}
              </Step>
            ))}
          </ol>
        </div>
      </section>

      {/* ─────────── 4. WHAT THE LETTER INCLUDES ─────────── */}
      <section id="whats-included" className="scroll-mt-24 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4 leading-[1.18] text-center">
            What the documentation includes
          </h2>
          <p className="text-[15px] text-slate-600 leading-relaxed max-w-[68ch] mx-auto text-center mb-8">
            Every document is written for a housing accommodation request and
            carries the details a housing provider needs to confirm it came from
            a real, actively licensed professional. People looking for a real ESA
            letter online should expect an individual professional review — not
            instant approval, a purchased certificate, or automatic registration.
          </p>
          {/* PROVIDER-CAROUSEL-STEPHANIE-IMAGE-ORDER-LIVE-001 (owner): the two
              plain bullet lists became two grouped cards with an icon per
              line. Every line of copy is unchanged. */}
          <div className="grid md:grid-cols-2 gap-5">
            <DocGroup
              icon="badge"
              tone="navy"
              title="From the reviewing professional"
              items={[
                { icon: "user", text: "The reviewing professional's full name and credential" },
                { icon: "id", text: "Their state license number and NPI" },
                { icon: "pen", text: "Their signature" },
                { icon: "calendar", text: "Issue date and expiration date" },
              ]}
            />
            <DocGroup
              icon="shield"
              tone="green"
              title="About you, and how it is verified"
              items={[
                { icon: "paw", text: "Your name and the animal the document covers" },
                { icon: "home", text: "Housing-accommodation language, not public-access language" },
                {
                  icon: "hash",
                  text: (
                    <>
                      A verification ID in the format <span className="font-mono text-slate-900 whitespace-nowrap">ESA-XX-XXXXXXX</span>
                    </>
                  ),
                },
                { icon: "link", text: "A verification link a landlord can check at pawtenant.com/verify" },
              ]}
            />
          </div>
        </div>
      </section>

      {/* ─────────── 4b. LICENSED PROFESSIONALS ───────────
          ESA-PSD-PROVIDER-CARDS-LIVE-001 (owner, 2026-09-23). Replaces the
          hardcoded three-card list, which kept showing a provider the admin
          had unpublished. This is the homepage's own DoctorsSection, fed by
          the curated set intersected with get_public_provider_directory();
          `requireLiveDirectory` means only a successful live read can put a
          card here — never the snapshot seed. Managed by website design in
          the section map (nothing here is studio-editable). */}
      <DoctorsSection
        id="licensed-professionals"
        sectionClassName="scroll-mt-24 py-14 md:py-16 bg-[#FDFBF7] border-b border-slate-200"
        eyebrow="Licensed Professionals"
        heading="Reviewed by licensed professionals"
        intro="Every ESA housing letter is reviewed and signed by a licensed mental health professional, with a public license record you can check yourself."
        footnote="Your assessment is matched to a professional licensed in your own state, who may or may not be one of the professionals shown here. Every license and NPI can be confirmed independently on the public NPPES registry."
        requireLiveDirectory
      />

      {/* ─────────── 5. HOUSING USE ─────────── */}
      <section id="housing-use" data-seo-section="housing_use" className="scroll-mt-24 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16">
          <div className="md:grid md:grid-cols-12 md:gap-10 md:items-center mb-6">
            <div className="md:col-span-7">
              <h2 data-seo-field="housing_use.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4 leading-[1.18]">
                {c.text("housing_use.heading")}
              </h2>
              <p data-seo-field="housing_use.intro" className="text-[15px] text-slate-600 leading-relaxed max-w-[68ch] mb-6 md:mb-0">
                {renderInline(c.text("housing_use.intro"))}
              </p>
            </div>
            <SectionPhoto
              className="md:col-span-5"
              webp="/assets/lifestyle/esa-cat-apartment-window.webp"
              jpg="/assets/lifestyle/esa-cat-apartment-window.jpg"
              alt="Tabby cat asleep on a sunny apartment windowsill"
              width={1000}
              height={750}
              aspect="aspect-[4/3]"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <div data-seo-field="housing_use.use_title" className="text-[14px] font-semibold text-slate-900 mb-2">{c.text("housing_use.use_title")}</div>
              <ul className="space-y-2 list-none p-0 m-0" data-seo-collection="housing_use.use">
                {useList.map((item, i) => <PlainPoint key={i}>{item.text}</PlainPoint>)}
              </ul>
            </div>
            <div className="bg-white border border-amber-200 rounded-xl p-5">
              <div data-seo-field="housing_use.claims_title" className="text-[14px] font-semibold text-slate-900 mb-2">{c.text("housing_use.claims_title")}</div>
              <ul className="space-y-2 list-none p-0 m-0" data-seo-collection="housing_use.claims">
                {claimsList.map((item, i) => <PlainPoint key={i}>{item.text}</PlainPoint>)}
              </ul>
            </div>
          </div>
          <p className="text-[12px] text-slate-500 leading-relaxed mt-5 max-w-[68ch]">
            PawTenant is not a law firm and does not provide legal advice. Whether
            a particular property is covered, and how a request is handled,
            depends on the property type, applicable state law, and the
            individual facts.
          </p>
        </div>
      </section>

      {/* ─────────── 6. TRANSPARENT PRICING ─────────── */}
      {/* ─────────── 6. TRANSPARENT PRICING ───────────
          ESA-HOUSING-HOMEPAGE-PRICING-PARITY-001 (owner, 2026-09-19).

          This used to be a bespoke two-card grid built only for this page. It
          is now the CANONICAL homepage pricing block: the same
          <PlanPricingSection> component, fed by the same buildEsaPlanCards() +
          ESA_PLAN_COPY from src/data/planPricingCards.ts that
          src/pages/home/components/HomePricingSection.tsx passes it, with the
          homepage's own eyebrow, heading, subheading, footnote and background.

          There is therefore ONE card implementation for both pages — a card,
          a price, a feature line or a CTA cannot change on one and not the
          other. Amounts still come from src/config/pricing.ts via
          planPricingCards; nothing here hardcodes a figure. PlanPricingSection
          also renders the shared PaymentTrustStrip below the cards, so this
          page no longer mounts its own (that would have been a second strip).

          `id`/`scroll-mt-24` are passed through so the #pricing anchor still
          clears the fixed navbar. CTA hrefs are the plain paths the homepage
          uses; PlanPricingSection appends attribution itself via
          withAttribution(), exactly as it does on the homepage. */}
      <PlanPricingSection
        theme="esa"
        id="pricing"
        className="scroll-mt-24 bg-[#fdf8f3] border-t border-orange-100"
        eyebrow={ESA_PLAN_COPY.eyebrow}
        heading={ESA_PLAN_COPY.heading}
        subheading={ESA_PLAN_COPY.subheading}
        cards={buildEsaPlanCards("/assessment")}
        footnote={ESA_PLAN_COPY.footnote}
      />

      {/* Pricing notes that sit BESIDE the cards rather than repeating them.
          The retired "Three pets" box was removed because the third homepage
          card already states that tier; what is left adds information the
          cards deliberately do not carry (the RA add-on is intentionally not a
          public pricing card) and the Klarna and PSD disclosures. */}
      <section data-seo-section="pricing_notes" className="bg-[#fdf8f3] border-b border-orange-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 pb-14 sm:pb-16">
          <div className="bg-white border border-orange-100 rounded-xl p-4 max-w-3xl mx-auto">
            <div data-seo-field="pricing_notes.ra_title" className="text-[13px] font-semibold text-slate-900 mb-1">{c.text("pricing_notes.ra_title")}</div>
            <p className="text-[13px] text-slate-600 leading-relaxed">
              If your landlord, property manager or HOA asks you to complete a
              separate accommodation form, that support is available at{" "}
              {RA_ONE_TIME} one-time or {RA_ANNUAL} per year. Most tenants only
              need the standard documentation.
            </p>
          </div>

          <p className="text-[12px] text-slate-500 leading-relaxed mt-5 max-w-3xl mx-auto">
            Klarna is available at checkout, subject to eligibility and Klarna's
            own payment terms. An instalment is a way of paying the price above —
            it is not a lower price.
          </p>

          <p className="text-[12px] text-slate-500 leading-relaxed mt-4 max-w-3xl mx-auto">
            Looking for a psychiatric service dog instead? A psychiatric service
            dog is a dog individually trained to perform tasks related to a
            disability, which is a different assessment.{" "}
            <Link to={PSD_ASSESSMENT_HREF} className="text-orange-600 font-medium hover:underline">
              Start the PSD assessment
            </Link>
            .
          </p>
        </div>
      </section>

      {/* ─────────── 7. WHY PAWTENANT ─────────── */}
      <section id="why-pawtenant" data-seo-section="why_pawtenant" className="scroll-mt-24 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16">
          <h2 data-seo-field="why_pawtenant.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-8 leading-[1.18]">
            {c.text("why_pawtenant.heading")}
          </h2>
          <div className="grid sm:grid-cols-2 gap-5" data-seo-collection="why_pawtenant.cards">
            {reasons.map((r) => (
              <ReasonCard key={r.key} title={r.title}>
                {renderInline(r.body)}
              </ReasonCard>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── 8. WHAT A LANDLORD CAN VERIFY ─────────── */}
      <section id="verify" data-seo-section="verify" className="scroll-mt-24 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-14 md:py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 data-seo-field="verify.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-3 leading-[1.18]">
              {c.text("verify.heading")}
            </h2>
            <p data-seo-field="verify.paragraph" className="text-[15px] text-slate-600 leading-relaxed max-w-[62ch] mb-6">
              {renderInline(c.text("verify.paragraph"))}
            </p>
            <ol className="list-none p-0 m-0" data-seo-collection="verify.steps">
              {verifySteps.map((s, i) => (
                <VerifyStep key={s.key} n={i + 1} title={s.title} tone={i === 0 ? "navy" : "green"} last={i === verifySteps.length - 1}>
                  {s.body.includes("ESA-XX-XXXXXXX")
                    ? <>{s.body.split("ESA-XX-XXXXXXX")[0]}<span className="font-mono">ESA-XX-XXXXXXX</span>{s.body.split("ESA-XX-XXXXXXX")[1] || null}</>
                    : renderInline(s.body)}
                </VerifyStep>
              ))}
            </ol>
          </div>
          <VerifyMock />
        </div>
      </section>

      {/* ─────────── 9. FAQ ─────────── */}
      <section id="faq" data-seo-section="faq" className="scroll-mt-24 bg-[#FAFAFA] border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-5 py-14 md:py-16">
          <h2 data-seo-field="faq.heading" className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-6 leading-[1.18] text-center">
            {c.text("faq.heading")}
          </h2>
          <div className="space-y-2.5" data-seo-collection="faq.items">
            {faqs.map((item, i) => (
              <div key={item.q} className={!showAllMobile && i >= 4 ? "hidden md:block" : ""}>
                <FAQItem q={item.q} a={item.a} defaultOpen={i === 0} />
              </div>
            ))}
          </div>
          {!showAllMobile && (
            <button
              type="button"
              onClick={() => setShowAllMobile(true)}
              data-seo-field="faq.show_more_label"
              className="md:hidden mt-4 w-full min-h-[44px] border border-slate-300 bg-white text-slate-800 text-[14px] font-medium rounded-md"
            >
              {c.text("faq.show_more_label")}
            </button>
          )}
        </div>
      </section>

      {/* ─────────── 10. FINAL CTA ─────────── */}
      <section data-seo-section="final_cta" className="relative bg-gradient-to-b from-[#0E2A47] to-[#091B30] text-white overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{ background: "radial-gradient(circle at 50% 0%, #ffffff 0%, transparent 60%)" }}
        />
        <div className="relative max-w-3xl mx-auto px-5 py-14 md:py-24 text-center">
          <h2 data-seo-field="final_cta.heading" className="text-2xl md:text-[28px] font-bold tracking-tight leading-tight mb-3">
            {c.text("final_cta.heading")}
          </h2>
          <p data-seo-field="final_cta.description" className="text-[14px] text-slate-300 leading-relaxed mb-7 max-w-[60ch] mx-auto">
            {renderInline(c.text("final_cta.description"))}
          </p>
          <Link
            to={ASSESSMENT_HREF}
            onClick={() => trackCtaClick(CTA_SECTION)}
            data-seo-field="final_cta.button_label"
            className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-[15px] md:text-[16px] px-8 py-4 rounded-md transition w-full sm:w-auto shadow-[0_4px_16px_rgba(249,115,22,0.35)]"
          >
            {c.text("final_cta.button_label")}
            <span aria-hidden>→</span>
          </Link>
          <div className="mt-9 grid sm:grid-cols-3 gap-4 text-left max-w-xl mx-auto" data-seo-collection="final_cta.trust">
            {finalTrust.map((t, i) => <FinalTrust key={i}>{t.text}</FinalTrust>)}
          </div>
        </div>
      </section>

      {/* ─────────── 11. ESA HOUSING RESOURCES ───────────
          PAWTENANT-ESA-HOUSING-GUIDES-PUBLISH-001 — links to the three ESA
          housing guides. Deliberately BELOW the final CTA, never in the hero. */}
      <EsaHousingGuidesSection
        heading="ESA housing resources"
        intro="Practical guides to documentation, the accommodation process, and common mistakes to avoid."
      />

      <PlannerMarketingSection family="esa" />

      <Hud2026UpdateBanner className="border-t border-gray-100" />

      <SharedFooter />

      {/* Persistent mobile conversion path — the same shared bar many other
          PawTenant pages use.
            to        — the ATTRIBUTED href, never the component's bare
                        "/assessment" default, so the bar carries UTM / gclid
                        exactly like every other CTA on this page.
            label     — the page's single primary CTA label. The component's own
                        default ("…From $115") is NOT used: anchoring on a lower
                        figure than the real one-time price is the documented
                        cause of the 2026-07-23 conversion collapse.
            consentSafe — sits BELOW the cookie banner and stays hidden until
                        consent is settled, so it can never cover the controls.
            hideNearBottomPx — retires the bar over the final CTA and footer. */}
      <MobileStickyApplyCTA
        to={ASSESSMENT_HREF}
        label="Start Free Assessment"
        icon="ri-shield-check-line"
        showAfterPx={500}
        consentSafe
        hideNearBottomPx={900}
        onClick={() => trackCtaClick(CTA_STICKY)}
      />
    </main>
  );
}
/* ────────────────────────── Sub-components (file-local) ────────────────────────── */

function LetterPreviewCard() {
  // The one document visual: the PawTenant ESA sample (SVG) inside a document
  // chrome card. Eager + high priority because it is the measured LCP element
  // on mobile; explicit width/height keep the reserved box stable (CLS 0).
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-[0_2px_8px_rgba(15,23,42,0.05)] overflow-hidden">
      <div className="flex items-center justify-between bg-slate-50 px-3 py-2 border-b border-slate-200">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
        </div>
        <div className="font-mono text-[10px] text-slate-500">esa-letter-sample.pdf</div>
        <div className="w-8" />
      </div>

      <div className="bg-white p-3 md:p-4">
        <img
          src="/images/checkout/esa-sample-letter.svg"
          alt="Sample PawTenant ESA letter showing provider credentials, and housing-accommodation language. Names and details are placeholders."
          width={800}
          height={1035}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="w-full h-auto block"
        />
      </div>

      <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-3 flex items-center gap-3">
        <span className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        </span>
        <div className="min-w-0">
          <div className="text-[12px] font-medium text-emerald-900 leading-tight">Every document carries a verification ID</div>
          <div className="text-[11px] text-emerald-800/80 leading-snug font-mono">pawtenant.com/verify · landlords confirm in seconds</div>
        </div>
      </div>

      <div className="text-center text-[10px] text-slate-400 py-2 px-3 bg-white border-t border-slate-100">
        Sample template · placeholder names · housing-accommodation language only.
      </div>
    </div>
  );
}

function TrustChip({
  label,
  detail,
  tone = "navy",
  icon = "shield",
}: {
  label: string;
  detail: string;
  tone?: "navy" | "green";
  icon?: "shield" | "badge" | "refund";
}) {
  const fillClasses =
    tone === "green"
      ? "bg-emerald-600 text-white shadow-[0_2px_6px_rgba(16,185,129,0.30)]"
      : "bg-[#0E2A47] text-white shadow-[0_2px_6px_rgba(14,42,71,0.30)]";

  return (
    <div className="flex items-start gap-3">
      <span className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${fillClasses}`}>
        {icon === "shield" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        )}
        {icon === "badge" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <circle cx="12" cy="8" r="6" />
            <path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" />
          </svg>
        )}
        {icon === "refund" && (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M3 12a9 9 0 1 0 3-6.7" />
            <path d="M3 4v5h5" />
          </svg>
        )}
      </span>
      <div className="min-w-0">
        <div className="text-[13.5px] font-semibold text-slate-900 leading-snug">{label}</div>
        <div className="text-[12px] text-slate-500 leading-snug">{detail}</div>
      </div>
    </div>
  );
}

/** A single bullet with a tick (green) or a neutral dash marker. */
function PlainPoint({ children, tone = "slate" }: { children: React.ReactNode; tone?: "slate" | "green" }) {
  return (
    <li className="flex gap-2.5 items-start text-[13px] leading-relaxed text-slate-700">
      <span
        aria-hidden
        className={`mt-[7px] w-1.5 h-1.5 rounded-full flex-shrink-0 ${tone === "green" ? "bg-emerald-600" : "bg-slate-400"}`}
      />
      <span>{children}</span>
    </li>
  );
}

function ReasonCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-[14.5px] font-semibold text-slate-900 mb-1.5">{title}</div>
      <div className="text-[13px] text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}

function OfferTick() {
  return (
    <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0" aria-hidden>
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

/** Mobile-hero trust-signal marker: an emerald disc on the dark overlay. */
function MobileTick() {
  return (
    <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-[0_1px_4px_rgba(0,0,0,0.25)]" aria-hidden>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

function VerifyStep({
  n,
  title,
  children,
  tone = "navy",
  last = false,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
  tone?: "navy" | "green";
  last?: boolean;
}) {
  const bubble = tone === "green" ? "bg-emerald-600" : "bg-[#0E2A47]";
  return (
    <li className={`relative flex gap-3.5 items-start ${last ? "" : "pb-4"}`}>
      {!last && <span aria-hidden className="absolute left-[13px] top-8 bottom-0 w-px bg-slate-200" />}
      <span className={`relative w-7 h-7 rounded-full ${bubble} text-white flex items-center justify-center text-[12.5px] font-semibold flex-shrink-0`}>{n}</span>
      <div className="min-w-0 pt-0.5">
        <div className="text-[14.5px] font-semibold text-slate-900 leading-snug">{title}</div>
        <div className="text-[13px] text-slate-600 leading-snug mt-0.5">{children}</div>
      </div>
    </li>
  );
}

function VerifyMock() {
  // Cropped real /verify result screen. Privacy-safe: no diagnosis or clinical
  // information is shown.
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(15,23,42,0.06)]">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
          <span className="w-2 h-2 rounded-full bg-slate-300" />
        </div>
        <div className="font-mono text-[10px] text-slate-500">pawtenant.com/verify</div>
        <div className="w-6" />
      </div>

      {/* ESA-HOUSING-HYBRID-TEST-001 — this is the owner-created verification
          snapshot and it must be served byte-exact, matching LIVE. Never route
          it through ResponsiveImage: its AVIF/WebP variants are lossy
          re-encodes of the screenshot, which counts as altering the owner's
          image. The bytes here outrank the Lighthouse score. */}
      <img
        src="/assets/ui/verification-cropped.png"
        alt="PawTenant verification result confirming a letter is authentic. Shows letter type, state, issue and expiration dates, issuing provider, NPI, and license. No patient health information is displayed."
        width={820}
        height={1110}
        loading="lazy"
        decoding="async"
        className="w-full h-auto block"
      />

      <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 flex items-center gap-2">
        <span className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </span>
        <div className="text-[11px] text-slate-600 leading-snug">
          Confirms <span className="text-slate-900 font-medium">authenticity only</span> — no patient health information is displayed.
        </div>
      </div>
    </div>
  );
}

// PROVIDER-CAROUSEL-STEPHANIE-IMAGE-ORDER-LIVE-001 — "What the documentation
// includes" cards. Inline SVG line icons: the RemixIcon font on this site is a
// SUBSET, so an off-subset glyph would paint blank.
type DocIconName = "badge" | "shield" | "user" | "id" | "pen" | "calendar" | "paw" | "home" | "hash" | "link";

const DOC_ICON_PATHS: Record<DocIconName, string[]> = {
  badge: ["M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12z", "M8.2 13.9 7 22l5-3 5 3-1.2-8.1"],
  shield: ["M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", "m9 12 2 2 4-4"],
  user: ["M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2", "M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "m16 11 2 2 4-4"],
  id: ["M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z", "M8 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4z", "M5 17a3 3 0 0 1 6 0", "M14 10h5", "M14 14h4"],
  pen: ["M12 20h9", "M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"],
  calendar: ["M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z", "M16 2v4", "M8 2v4", "M3 10h18", "m9 16 2 2 4-4"],
  paw: ["M11 5.5a2 2.5 0 1 1-4 0 2 2.5 0 0 1 4 0z", "M17 5.5a2 2.5 0 1 1-4 0 2 2.5 0 0 1 4 0z", "M6 11a2 2.5 0 1 1-4 0 2 2.5 0 0 1 4 0z", "M22 11a2 2.5 0 1 1-4 0 2 2.5 0 0 1 4 0z", "M12 12c-3 0-6 4.5-6 7 0 1.7 1.3 2.5 3 2.5 1.2 0 2-.6 3-.6s1.8.6 3 .6c1.7 0 3-.8 3-2.5 0-2.5-3-7-6-7z"],
  home: ["m3 10 9-7 9 7", "M5 9v11a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9"],
  hash: ["M4 9h16", "M4 15h16", "M10 3 8 21", "M16 3l-2 18"],
  link: ["M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7", "M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"],
};

function DocIcon({ name, className = "" }: { name: DocIconName; className?: string }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden className={className}>
      {DOC_ICON_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

// `titleField` / `collectionId` carry the section-map ids through to the
// rendered h3 / ul as data-seo-field / data-seo-collection, so the studio's
// binding guard finds the literal ids on the page and the editor can point
// at the exact elements. Both are optional; omitted, the markup is unchanged.
function DocGroup({ icon, tone, title, items }: {
  icon: DocIconName;
  tone: "navy" | "green";
  title: string;
  items: { icon: DocIconName; text: ReactNode }[];
}) {
  const badge = tone === "navy" ? "bg-[#0E2A47] text-white" : "bg-emerald-600 text-white";
  const chip =
    tone === "navy"
      ? "bg-slate-50 border-slate-200 text-[#0E2A47]"
      : "bg-emerald-50 border-emerald-200 text-emerald-700";
  return (
    <div data-pt-doc-group className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="flex items-center gap-3 pb-4 mb-1 border-b border-slate-100">
        <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${badge}`}>
          <DocIcon name={icon} />
        </span>
        <h3 className="text-[15px] md:text-[16px] font-semibold text-slate-900 leading-snug">{title}</h3>
      </div>
      <ul className="list-none p-0 m-0 divide-y divide-slate-100">
        {items.map((item) => (
          <li key={item.icon} className="flex items-center gap-3 py-3">
            <span className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${chip}`}>
              <DocIcon name={item.icon} />
            </span>
            <span className="text-[14.5px] text-slate-700 leading-snug">{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ESA-PSD-PROVIDER-CARDS-LIVE-001 — below-the-fold section photo. Local assets
// only (already in use on the homepage/blog), explicit width/height plus a
// fixed aspect box so nothing shifts when it loads, lazy because every use is
// below the fold.
function SectionPhoto({ webp, jpg, alt, width, height, aspect, className = "" }: {
  webp: string;
  jpg: string;
  alt: string;
  width: number;
  height: number;
  aspect: string;
  className?: string;
}) {
  return (
    <figure data-pt-section-photo className={`m-0 ${className}`}>
      <div className={`${aspect} w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-100`}>
        <picture>
          <source type="image/webp" srcSet={webp} />
          <img
            src={jpg}
            alt={alt}
            width={width}
            height={height}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </picture>
      </div>
    </figure>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <li className="flex gap-3 items-start">
      <div className="w-9 h-9 rounded-full bg-[#0E2A47] text-white flex items-center justify-center text-[14px] font-medium flex-shrink-0">{n}</div>
      <div>
        <div className="text-[14.5px] font-semibold text-slate-900 mb-1">{title}</div>
        <div className="text-[13px] text-slate-600 leading-relaxed">{children}</div>
      </div>
    </li>
  );
}

function FAQItem({ q, a, defaultOpen }: { q: string; a: string; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const accent = defaultOpen
    ? open
      ? "bg-[#0E2A47]/[0.03] border-[#0E2A47]/30"
      : "bg-white border-[#0E2A47]/30"
    : "bg-white border-slate-200";
  const iconColor = open ? "text-emerald-600" : "text-[#0E2A47]";

  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
      className={`group rounded-lg px-4 py-1 border transition-colors ${accent}`}
    >
      {/* min-h-[44px] makes the whole collapsed row the tap target without
          changing how it looks. data-pt-faq-q / data-pt-faq-a mark the
          question and answer text for the FAQ-parity guard; every answer is
          in the DOM whether or not the row is open. */}
      <summary className="flex min-h-[44px] items-center justify-between gap-3 py-2 cursor-pointer list-none">
        <span data-pt-faq-q className="text-[13.5px] font-medium text-slate-900 leading-snug">{q}</span>
        <span
          aria-hidden
          className={`text-[18px] leading-none flex-shrink-0 transition-transform duration-200 ${iconColor} ${open ? "rotate-45" : ""}`}
        >
          +
        </span>
      </summary>
      <div data-pt-faq-a className="text-[13px] text-slate-600 leading-relaxed mt-3 pt-3 border-t border-slate-100">{renderInline(a)}</div>
    </details>
  );
}

function FinalTrust({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 text-[12.5px] text-slate-300 leading-relaxed">
      <span className="w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span>{children}</span>
    </div>
  );
}
